/**
 * Data Provider
 *
 * Notify-backend adapter for React Admin.
 *
 * Backend conventions (from Angular services):
 *   GET    /{resource}-with-pagination?draw&start&length&search&orderBy&column
 *            -> { Response: { draw, recordsTotal, recordsFiltered, data[] } }
 *   GET    /{resource}/{id}                       -> { Response: <record> }
 *   POST   /{resource}      body = { ...data }    -> { Response: <record> }    // create (no id)
 *   POST   /{resource}      body = { id, ...data} -> { Response: <record> }    // update (single endpoint)
 *   DELETE /{resource}/{id}                       -> { Response: true } | empty
 *
 * Errors come back as { Error: string } with a non-2xx status; we surface
 * that to React Admin as a thrown Error whose `.message` is the backend
 * text so useNotify renders it verbatim.
 *
 * Resource-to-endpoint overrides live in `resourceEndpoints`. Anything
 * not in the map defaults to `/{resource}` for singular ops and
 * `/{resource}-with-pagination` for list.
 */

import type { DataProvider, GetListParams, GetListResult } from "react-admin";
import { httpClient, HttpError } from "@/utils/httpClient";
import { formatErrorBody } from "@/utils/extractErrorMessage";
import { emitResourceMutated } from "@/utils/resourceEvents";

interface NotifyEnvelope<T> {
  Response: T;
}

interface NotifyListPayload<T> {
  draw?: number;
  recordsTotal: number;
  recordsFiltered?: number;
  data: T[];
}

interface RecordLike {
  id?: string | number;
  [key: string]: unknown;
}

interface ResourceEndpointOverride {
  base?: string;
  list?: string;
  updateMethod?: "POST" | "PUT";
  synthesizeId?: boolean;
  headerFromFilter?: string;
}

const resourceEndpoints: Record<string, ResourceEndpointOverride> = {
  "apps/accounts": { base: "/account", list: "/account-with-pagination" },
  applications: { base: "/application", list: "/application-with-pagination" },
  licenses: { base: "/license", list: "/License-with-pagination" },
  providers: {
    base: "/provider",
    list: "/provider-with-pagination",
    updateMethod: "PUT",
  },
  "default-pricing": { list: "/default-pricing/with-pagination" },
  "credit-consumption/cost-center": {
    base: "/costcenter",
    list: "/costcenter/with-pagination",
  },
  "credit-consumption/pricing": {
    base: "/pricing",
    list: "/pricing/with-pagination",
  },
  "credit-consumption/credit-balance": {
    base: "/credit-balances",
    list: "/credit-balances/with-pagination",
  },
  "credit-consumption/credit-transaction": {
    base: "/credit-transactions",
    list: "/credit-transactions/with-pagination",
  },
  "credit-consumption/credit-event-logs": {
    base: "/CreditMessageLog",
    list: "/CreditMessageLog/with-pagination",
    synthesizeId: true,
  },
  "credit-consumption/credit-event-summary": {
    base: "/CreditMessageLog",
    list: "/CreditMessageLog/summary-with-pagination",
    synthesizeId: true,
  },
  "user-management": {
    base: "/user",
    list: "/user",
  },
  // Usuarios del backoffice. Solo el listado pasa por aqui; las escrituras
  // van por rutas dedicadas (platformUserService), como en user-management.
  "platform-users": {
    base: "/platform-user",
    list: "/platform-user",
  },
  "scheduled-jobs": {
    base: "/v1/schedules-with-pagination",
    list: "/v1/schedules-with-pagination",
    headerFromFilter: "accountId",
    synthesizeId: true,
  },
  statistics: {
    base: "/account/account-statistic",
    list: "/account/account-statistic",
    synthesizeId: true,
  },
  "notification-logs": {
    base: "/account/notification-logs",
    list: "/account/notification-logs",
    synthesizeId: true,
  },
  "manage-groups": {
    base: "/group",
    list: "/group-with-pagination",
  },
};

const updateMethodFor = (resource: string): "POST" | "PUT" =>
  resourceEndpoints[resource]?.updateMethod ?? "POST";

const resourceIdFields: Record<string, string> = {
  "credit-consumption/credit-balance": "costCenterId",
};

const baseFor = (resource: string): string =>
  resourceEndpoints[resource]?.base ?? `/${resource}`;

const listFor = (resource: string): string =>
  resourceEndpoints[resource]?.list ?? `/${resource}-with-pagination`;

const buildListQuery = (params: GetListParams): string => {
  const { pagination, sort, filter } = params;
  const page = pagination?.page ?? 1;
  const perPage = pagination?.perPage ?? 10;
  const search = typeof filter?.q === "string" ? filter.q : "";

  const qs = new URLSearchParams({
    draw: "1",
    start: String((page - 1) * perPage),
    length: String(perPage),
    search,
    orderBy: (sort?.order ?? "ASC").toLowerCase(),
    column: sort?.field ?? "",
  });

  Object.entries(filter ?? {}).forEach(([key, value]) => {
    if (key === "q" || value == null) return;
    qs.append(key, String(value));
  });

  return qs.toString();
};

const pickId = (
  raw: RecordLike | null | undefined,
  resource?: string,
): string | number | undefined => {
  if (raw == null) return undefined;
  const override = resource ? resourceIdFields[resource] : undefined;
  if (override) {
    const v = (raw as Record<string, unknown>)[override];
    if (typeof v === "string" || typeof v === "number") return v;
  }
  if (resource === "apps/accounts") {
    const ar = (raw as { accountResponse?: { id?: string | number } })
      .accountResponse;
    if (ar && (typeof ar.id === "string" || typeof ar.id === "number")) {
      return ar.id;
    }
  }
  if (raw.id != null) return raw.id as string | number;
  const anyRaw = raw as Record<string, unknown>;
  if (typeof anyRaw.Id === "string" || typeof anyRaw.Id === "number")
    return anyRaw.Id;
  if (typeof anyRaw.ID === "string" || typeof anyRaw.ID === "number")
    return anyRaw.ID;
  return undefined;
};

const coerceRecord = <T extends RecordLike>(
  raw: T,
  resource?: string,
): T & { id: string | number } => {
  const id = pickId(raw, resource);
  if (id == null) {
    throw new Error("Notify record is missing `id` field");
  }
  return { ...raw, id } as T & { id: string | number };
};

const rethrow = (error: unknown): never => {
  if (error instanceof HttpError) {
    const fromBody = formatErrorBody(error.data);
    const message =
      fromBody ??
      (error.message && error.message !== error.status.toString()
        ? error.message
        : `Request failed with status ${error.status}`);
    const wrapped = new Error(message);
    (wrapped as Error & { status?: number }).status = error.status;
    throw wrapped;
  }
  throw error instanceof Error ? error : new Error(String(error));
};

export const dataProvider: DataProvider = {
  getList: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: GetListParams,
  ): Promise<GetListResult<T & { id: string | number }>> => {
    try {
      const headerKey = resourceEndpoints[resource]?.headerFromFilter;
      const requestHeaders: Record<string, string> = {};
      let workingParams: GetListParams = params;
      if (
        headerKey &&
        params.filter &&
        params.filter[headerKey] != null &&
        params.filter[headerKey] !== ""
      ) {
        requestHeaders["account-id"] = String(params.filter[headerKey]);
        const { [headerKey]: _consumed, ...restFilter } = params.filter;
        workingParams = { ...params, filter: restFilter };
      }
      const qs = buildListQuery(workingParams);
      const { data } = await httpClient.get<
        NotifyEnvelope<NotifyListPayload<T>>
      >(
        `${listFor(resource)}?${qs}`,
        Object.keys(requestHeaders).length > 0
          ? { headers: requestHeaders }
          : undefined,
      );
      const payload = data.Response;
      const synthesize = resourceEndpoints[resource]?.synthesizeId === true;
      const startIdx =
        ((params.pagination?.page ?? 1) - 1) *
        (params.pagination?.perPage ?? 10);
      return {
        data: payload.data.map((r, i) => {
          if (synthesize && pickId(r, resource) == null) {
            return { ...r, id: startIdx + i } as T & { id: string | number };
          }
          return coerceRecord(r, resource);
        }),
        total: payload.recordsFiltered ?? payload.recordsTotal,
      };
    } catch (error) {
      return rethrow(error);
    }
  },

  getOne: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: { id: string | number },
  ) => {
    try {
      const { data } = await httpClient.get<NotifyEnvelope<T>>(
        `${baseFor(resource)}/${encodeURIComponent(String(params.id))}`,
      );
      return { data: coerceRecord(data.Response, resource) };
    } catch (error) {
      return rethrow(error);
    }
  },

  getMany: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: { ids: (string | number)[] },
  ) => {
    try {
      const results = await Promise.all(
        params.ids.map((id) =>
          httpClient.get<NotifyEnvelope<T>>(
            `${baseFor(resource)}/${encodeURIComponent(String(id))}`,
          ),
        ),
      );
      return {
        data: results.map((r) => coerceRecord(r.data.Response, resource)),
      };
    } catch (error) {
      return rethrow(error);
    }
  },

  getManyReference: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: GetListParams & { target: string; id: string | number },
  ): Promise<GetListResult<T & { id: string | number }>> => {
    // Apply the reference as a filter on the standard list call.
    const listParams: GetListParams = {
      ...params,
      filter: { ...params.filter, [params.target]: params.id },
    };
    return dataProvider.getList<T & { id: string | number }>(
      resource,
      listParams,
    );
  },

  create: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: { data: Partial<T> | FormData },
  ) => {
    try {
      const isMultipart = params.data instanceof FormData;
      const { data } = await httpClient.post<NotifyEnvelope<T>>(
        baseFor(resource),
        params.data,
      );
      const response = (data?.Response ?? {}) as Partial<T>;
      const submitted = (isMultipart ? {} : params.data) as Partial<T>;
      const merged = { ...submitted, ...response } as T;
      const id = pickId(merged as RecordLike, resource);
      emitResourceMutated(resource);
      return {
        data: { ...merged, id: id ?? `tmp-${Date.now()}` } as T & {
          id: string | number;
        },
      };
    } catch (error) {
      return rethrow(error);
    }
  },

  update: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: { id: string | number; data: Partial<T> | FormData },
  ) => {
    try {
      const method = updateMethodFor(resource);
      const isMultipart = params.data instanceof FormData;
      const body: unknown = isMultipart
        ? params.data
        : { id: params.id, ...(params.data as object) };
      const send = method === "PUT" ? httpClient.put : httpClient.post;
      const { data } = await send<NotifyEnvelope<T>>(baseFor(resource), body);
      const response = (data?.Response ?? {}) as Partial<T>;
      const submitted = (isMultipart ? {} : params.data) as Partial<T>;
      emitResourceMutated(resource);
      return {
        data: { ...submitted, ...response, id: params.id } as T & {
          id: string | number;
        },
      };
    } catch (error) {
      return rethrow(error);
    }
  },

  updateMany: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: { ids: (string | number)[]; data: Partial<T> },
  ) => {
    try {
      await Promise.all(
        params.ids.map((id) =>
          httpClient.post<NotifyEnvelope<T>>(baseFor(resource), {
            id,
            ...params.data,
          }),
        ),
      );
      emitResourceMutated(resource);
      return { data: params.ids };
    } catch (error) {
      return rethrow(error);
    }
  },

  delete: async <T extends RecordLike = RecordLike>(
    resource: string,
    params: { id: string | number; previousData?: T },
  ) => {
    try {
      await httpClient.delete<NotifyEnvelope<unknown>>(
        `${baseFor(resource)}/${encodeURIComponent(String(params.id))}`,
      );
      emitResourceMutated(resource);
      return {
        data: (params.previousData ?? { id: params.id }) as T & {
          id: string | number;
        },
      };
    } catch (error) {
      return rethrow(error);
    }
  },

  deleteMany: async (
    resource: string,
    params: { ids: (string | number)[] },
  ) => {
    try {
      await Promise.all(
        params.ids.map((id) =>
          httpClient.delete<NotifyEnvelope<unknown>>(
            `${baseFor(resource)}/${encodeURIComponent(String(id))}`,
          ),
        ),
      );
      emitResourceMutated(resource);
      return { data: params.ids };
    } catch (error) {
      return rethrow(error);
    }
  },
};

export default dataProvider;
